// @ts-nocheck
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { fetchBreakoutRooms, fetchTeacherSubjectsWithStudents, fetchAdminSubjectsWithStudents, createBreakoutRoom, updateBreakoutRoom, addStudentToBreakoutRoom, removeStudentFromBreakoutRoom, deleteBreakoutRoom } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { sortStudentsByName } from '../utils/studentSort';

const ADMIN_ROLES = ['admin', 'school_admin', 'district_admin', 'superadmin'];

function memberLabel(member, students) {
  const fromApi = `${member.first_name || ''} ${member.last_name || ''}`.trim();
  if (fromApi) return fromApi;
  const fromRoster = students.find((st) => String(st.student_id) === String(member.student_id))?.label;
  return fromRoster || `Student ${member.student_id}`;
}


const GroupsManager = forwardRef(function GroupsManager({ classId, students }: any, ref) {
    // Expose saveAllGroups for parent integration
    useImperativeHandle(ref, () => ({
      saveAllGroups: async () => {
        // This is a placeholder for future batch group save logic.
        // If you want to batch-create/update groups, implement here.
        // For now, this is a no-op (returns true).
        return true;
      }
    }), []);
  const [groups, setGroups] = useState<any>([]);
  const [newGroupName, setNewGroupName] = useState<any>('');
  const [newGroupColor, setNewGroupColor] = useState('#eab308');
  const [loading, setLoading] = useState<any>(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState<any>([]);
  const [editingGroupId, setEditingGroupId] = useState<any>(null);
  const [editingGroupName, setEditingGroupName] = useState<any>('');
  const [editingGroupColor, setEditingGroupColor] = useState('#eab308');
  const [actionError, setActionError] = useState<any>('');
  const [actionMessage, setActionMessage] = useState<any>('');
  const [savingAction, setSavingAction] = useState<any>(false);
  const [teacherClasses, setTeacherClasses] = useState<any>([]);
  const [selectedDestinationSubjects, setSelectedDestinationSubjects] = useState<any>([]);
  const [selectedDestinationClassIds, setSelectedDestinationClassIds] = useState<any>([]);
  const { user } = useAuth();
  const userRoles = [...(user?.roles || []), user?.role].filter(Boolean);
  const isAdminViewer = userRoles.some((role) => ADMIN_ROLES.includes(role)) && !userRoles.includes('teacher');

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBreakoutRooms(classId);
      setGroups(data);
    } catch (error: any) {
      setActionError(error.message || 'Could not load groups.');
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    let cancelled = false;
    const loadClasses = isAdminViewer ? fetchAdminSubjectsWithStudents : fetchTeacherSubjectsWithStudents;
    loadClasses()
      .then((data) => {
        if (cancelled) return;
        const classRows = (data.subjects || []).flatMap((subject) => (subject.classes || []).map((cls) => ({
          ...cls,
          subject_name: subject.subject_name || 'Subject',
        }))).filter((cls) => String(cls.class_id) !== String(classId));
        setTeacherClasses(classRows);
      })
      .catch(() => {
        // Duplication is optional; hide it rather than show an error if the class list can't load.
        if (!cancelled) setTeacherClasses([]);
      });
    return () => { cancelled = true; };
  }, [classId, isAdminViewer]);

  async function handleCreateGroup() {
    const name = newGroupName.trim();
    if (!name) {
      setActionError('Enter a group name before creating it.');
      return;
    }
    setSavingAction(true);
    setActionError('');
    setActionMessage('');
    try {
      const group = await createBreakoutRoom({ name, color: newGroupColor }, classId);
      for (const studentId of selectedStudentIds) {
        await addStudentToBreakoutRoom(group.breakout_room_id, { student_id: studentId });
      }
      setNewGroupName('');
      setSelectedStudentIds([]);
      setActionMessage(`Created ${name}.`);
      await loadGroups();
    } catch (error: any) {
      setActionError(error.message || 'Could not create group.');
    } finally {
      setSavingAction(false);
    }
  }

  async function handleAddStudent(groupId, studentId) {
    if (!studentId) return;
    setSavingAction(true);
    setActionError('');
    try {
      await addStudentToBreakoutRoom(groupId, { student_id: studentId });
      await loadGroups();
    } catch (error: any) {
      setActionError(error.message || 'Could not add student to group.');
    } finally {
      setSavingAction(false);
    }
  }

  async function handleRemoveStudent(groupId, studentId) {
    setSavingAction(true);
    setActionError('');
    try {
      await removeStudentFromBreakoutRoom(groupId, studentId);
      await loadGroups();
    } catch (error: any) {
      setActionError(error.message || 'Could not remove student from group.');
    } finally {
      setSavingAction(false);
    }
  }

  async function handleDeleteGroup(groupId) {
    if (!window.confirm('Delete this group? Students will remain in the class but become unassigned.')) return;
    setSavingAction(true);
    setActionError('');
    try {
      await deleteBreakoutRoom(groupId);
      setActionMessage('Group deleted.');
      await loadGroups();
    } catch (error: any) {
      setActionError(error.message || 'Could not delete group.');
    } finally {
      setSavingAction(false);
    }
  }

  async function handleUpdateGroup(groupId) {
    if (!editingGroupName.trim()) {
      setActionError('Group name cannot be empty.');
      return;
    }
    setSavingAction(true);
    setActionError('');
    try {
      await updateBreakoutRoom(groupId, { name: editingGroupName.trim(), color: editingGroupColor });
      setEditingGroupId(null);
      setActionMessage('Group updated.');
      await loadGroups();
    } catch (error: any) {
      setActionError(error.message || 'Could not update group.');
    } finally {
      setSavingAction(false);
    }
  }

  const destinationSubjects = Array.from(new Set(teacherClasses.map((cls) => cls.subject_name))).sort((a, b) => a.localeCompare(b));
  const destinationClasses = teacherClasses.filter((cls) => selectedDestinationSubjects.includes(cls.subject_name));

  const toggleDestinationSubject = (subjectName) => {
    setSelectedDestinationSubjects((previous) => {
      const next = previous.includes(subjectName)
        ? previous.filter((name) => name !== subjectName)
        : [...previous, subjectName];
      const allowedClassIds = new Set(teacherClasses.filter((cls) => next.includes(cls.subject_name)).map((cls) => String(cls.class_id)));
      setSelectedDestinationClassIds((selected) => selected.filter((id) => allowedClassIds.has(String(id))));
      return next;
    });
  };

  const toggleDestinationClass = (classIdValue) => {
    const value = String(classIdValue);
    setSelectedDestinationClassIds((previous) => previous.includes(value)
      ? previous.filter((id) => id !== value)
      : [...previous, value]);
  };

  async function handleDuplicateGroups() {
    const targetClasses = destinationClasses.filter((cls) => selectedDestinationClassIds.includes(String(cls.class_id)));
    if (!groups.length || !targetClasses.length) return;

    setSavingAction(true);
    setActionError('');
    setActionMessage('');
    let copiedGroupCount = 0;
    let skippedGroupCount = 0;
    try {
      for (const targetClass of targetClasses) {
        const targetStudentIds = new Set((targetClass.students || []).map((student) => String(student.student_id)));
        const targetGroups = await fetchBreakoutRooms(targetClass.class_id);
        const existingNames = new Set((targetGroups || []).map((group) => String(group.name || '').trim().toLowerCase()));
        for (const sourceGroup of groups) {
          const normalizedName = String(sourceGroup.name || '').trim().toLowerCase();
          if (existingNames.has(normalizedName)) {
            skippedGroupCount += 1;
            continue;
          }
          const copiedGroup = await createBreakoutRoom({ name: sourceGroup.name, color: sourceGroup.color }, targetClass.class_id);
          copiedGroupCount += 1;
          existingNames.add(normalizedName);
          const sharedStudentIds = (sourceGroup.students || [])
            .map((student) => student.student_id)
            .filter((studentId) => targetStudentIds.has(String(studentId)));
          for (const studentId of sharedStudentIds) {
            await addStudentToBreakoutRoom(copiedGroup.breakout_room_id, { student_id: studentId });
          }
        }
      }
      setActionMessage(`Copied ${copiedGroupCount} group${copiedGroupCount === 1 ? '' : 's'} to ${targetClasses.length} class${targetClasses.length === 1 ? '' : 'es'}${skippedGroupCount ? `; skipped ${skippedGroupCount} same-name group${skippedGroupCount === 1 ? '' : 's'}` : ''}. Memberships were copied for students shared with each class.`);
      setSelectedDestinationClassIds([]);
    } catch (error: any) {
      setActionError(error.message || 'Could not duplicate all groups. Some groups may already have been copied.');
    } finally {
      setSavingAction(false);
    }
  }

  const availableStudentsForGroup = (group) => sortStudentsByName(students.filter(
    (student) => !(group?.students || []).some((member) => member.student_id === student.student_id)
  ), 'first');
  const assignedStudentIds = new Set(groups.flatMap((group) => (group.students || []).map((student) => student.student_id)));
  const unassignedStudents = students.filter((student) => !assignedStudentIds.has(student.student_id));

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <h3 style={{ fontWeight: 800, fontSize: 21, color: '#1e3a5f', margin: 0 }}>Class groups</h3>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Organize students for targeted instruction and quick grading.</div>
        </div>
        <span role="status" style={{ color: '#64748b', fontSize: 12, fontWeight: 700 }}>{loading ? 'Updating groups...' : `${groups.length} group${groups.length === 1 ? '' : 's'}`}</span>
      </div>
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 20,
        alignItems: 'center',
        flexWrap: 'wrap',
        background: 'linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%)',
        border: '1px solid #d9e5fb',
        borderRadius: 14,
        padding: '14px 16px',
        boxShadow: '0 5px 16px rgba(30, 58, 95, 0.06)',
      }}>
        <input
          value={newGroupName}
          onChange={e => setNewGroupName(e.target.value)}
          placeholder="Group name (e.g. Red Group)"
          style={{
            border: '1.5px solid #c7d7fc',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 14,
            flex: '1 1 190px',
            minWidth: 180,
            background: 'white',
            outline: 'none',
          }}
        />
        <input
          type="color"
          value={newGroupColor}
          onChange={e => setNewGroupColor(e.target.value)}
          style={{ width: 36, height: 36, border: 'none', background: 'none', cursor: 'pointer' }}
        />
        <select
          multiple
          value={selectedStudentIds.map(String)}
          onChange={e => setSelectedStudentIds(Array.from(e.target.selectedOptions).map(o => Number(o.value)))}
          style={{
            flex: '1 1 170px',
            minWidth: 170,
            minHeight: 42,
            border: '1.5px solid #c7d7fc',
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 14,
            background: 'white',
            outline: 'none',
          }}
        >
          {students.map(s => (
            <option key={s.student_id} value={s.student_id}>{s.label}</option>
          ))}
        </select>
        <button
          onClick={handleCreateGroup}
          disabled={!newGroupName.trim() || savingAction}
          style={{
            background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            padding: '10px 22px',
            fontWeight: 700,
            fontSize: 14,
            cursor: newGroupName.trim() && !savingAction ? 'pointer' : 'not-allowed',
            boxShadow: '0 1px 4px #0001',
            transition: 'background 0.15s',
          }}
        >
          {savingAction ? 'Saving...' : 'Create Group'}
        </button>
      </div>
      {actionMessage && <div style={{ margin: '-8px 0 14px', padding: '9px 11px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, color: '#166534', fontSize: 13, fontWeight: 600 }}>{actionMessage}</div>}
      {actionError && <div style={{ margin: '-8px 0 14px', padding: '9px 11px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{actionError}</div>}
      {groups.length > 0 && teacherClasses.length > 0 && (
        <section style={{ marginBottom: 20, padding: 14, background: '#f8fbff', border: '1px solid #d9e5fb', borderRadius: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
            <div>
              <div style={{ color: '#1e3a5f', fontSize: 14, fontWeight: 800 }}>Duplicate these groups</div>
              <div style={{ color: '#64748b', fontSize: 12, marginTop: 3 }}>Choose subjects, then destination classes. Existing same-name groups are skipped; only students shared with a destination class are copied.</div>
            </div>
            <button
              type="button"
              onClick={handleDuplicateGroups}
              disabled={savingAction || !selectedDestinationClassIds.length}
              style={{ flex: '0 0 auto', background: selectedDestinationClassIds.length ? '#2563eb' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 800, cursor: savingAction || !selectedDestinationClassIds.length ? 'not-allowed' : 'pointer' }}
            >
              {savingAction ? 'Copying…' : 'Copy Groups'}
            </button>
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            <div>
              <div style={{ marginBottom: 5, color: '#475569', fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>1. Select subject(s)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {destinationSubjects.map((subjectName) => {
                  const selected = selectedDestinationSubjects.includes(subjectName);
                  return (
                    <label key={subjectName} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 9px', border: `1px solid ${selected ? '#7aa2e3' : '#dbe4f0'}`, borderRadius: 8, background: selected ? '#eaf2ff' : '#fff', color: '#334155', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      <input type="checkbox" checked={selected} onChange={() => toggleDestinationSubject(subjectName)} />
                      {subjectName}
                    </label>
                  );
                })}
              </div>
            </div>
            {selectedDestinationSubjects.length > 0 && (
              <div>
                <div style={{ marginBottom: 5, color: '#475569', fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>2. Select destination class(es)</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 6 }}>
                  {destinationClasses.map((targetClass) => {
                    const id = String(targetClass.class_id);
                    return (
                      <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 9px', border: '1px solid #dbe4f0', borderRadius: 8, background: '#fff', color: '#334155', fontSize: 12, cursor: 'pointer' }}>
                        <input type="checkbox" checked={selectedDestinationClassIds.includes(id)} onChange={() => toggleDestinationClass(id)} />
                        <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{targetClass.subject_name} · {targetClass.class_name || `Class ${id}`}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
      {loading && groups.length === 0 ? (
        <div style={{ color: '#6b7280', padding: 16 }}>Loading...</div>
      ) : (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {groups.map(group => (
            <div key={group.breakout_room_id} style={{
              border: '1px solid #dbe5f3',
              borderTop: `4px solid ${group.color}`,
              borderRadius: 14,
              padding: 16,
              flex: '1 1 260px',
              maxWidth: 360,
              background: '#fff',
              boxShadow: '0 6px 18px rgba(30, 58, 95, 0.08)',
              marginBottom: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                {editingGroupId === group.breakout_room_id ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input value={editingGroupName} onChange={e => setEditingGroupName(e.target.value)} style={{ width: 130, padding: '7px 8px', border: '1px solid #93b4ed', borderRadius: 7, fontWeight: 700 }} />
                    <input type="color" value={editingGroupColor} onChange={e => setEditingGroupColor(e.target.value)} style={{ width: 28, height: 28, border: 'none' }} />
                    <button type="button" onClick={() => handleUpdateGroup(group.breakout_room_id)} style={{ color: '#166534', border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 6, padding: '5px 8px', fontWeight: 700, cursor: 'pointer' }}>Save</button>
                    <button type="button" onClick={() => setEditingGroupId(null)} style={{ color: '#64748b', border: '1px solid #e2e8f0', background: '#fff', borderRadius: 6, padding: '5px 8px', cursor: 'pointer' }}>Cancel</button>
                  </div>
                ) : <span style={{ fontWeight: 800, color: '#1e3a5f', fontSize: 17 }}>{group.name}</span>}
                {editingGroupId !== group.breakout_room_id && <button type="button" onClick={() => { setEditingGroupId(group.breakout_room_id); setEditingGroupName(group.name); setEditingGroupColor(group.color); }} style={{ color: '#2563eb', background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 6, padding: '5px 8px', fontWeight: 700, cursor: 'pointer' }}>Edit</button>}
                <button
                  type="button"
                  onClick={() => handleDeleteGroup(group.breakout_room_id)}
                  style={{
                    color: '#dc2626',
                    background: 'none',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                  }}
                  title="Delete group"
                >✕</button>
              </div>
              <div style={{ marginTop: 4, marginBottom: 8 }}>
                <strong style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.7 }}>Students · {group.students.length}</strong>
                <ul style={{ paddingLeft: 0, listStyle: 'none', margin: '9px 0 12px', display: 'grid', gap: 6 }}>
                  {group.students.map(s => (
                    <li key={s.student_id} style={{ fontSize: 13, color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span>{memberLabel(s, students)}</span>
                      <button
                        onClick={() => handleRemoveStudent(group.breakout_room_id, s.student_id)}
                        style={{
                          marginLeft: 10,
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: 6,
                          padding: '2px 10px',
                          fontWeight: 600,
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                        title="Remove student"
                      >Remove</button>
                    </li>
                  ))}
                </ul>
                <select
                  onChange={e => handleAddStudent(group.breakout_room_id, parseInt(e.target.value))}
                  value=""
                  style={{
                    width: '100%',
                    border: '1.5px solid #c7d7fc',
                    borderRadius: 8,
                    padding: '8px 10px',
                    fontSize: 13,
                    background: 'white',
                    outline: 'none',
                  }}
                >
                  <option value="">Add student…</option>
                  {availableStudentsForGroup(group).map(s => (
                    <option key={s.student_id} value={s.student_id}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 24 }}>
        <strong style={{ color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.7 }}>Unassigned students · {unassignedStudents.length}</strong>
        <ul style={{ paddingLeft: 18, margin: '8px 0', color: '#64748b' }}>
          {unassignedStudents.map(s => <li key={s.student_id} style={{ fontSize: 14, color: '#6b7280' }}>{s.label}</li>)}
        </ul>
      </div>
    </div>
  );
});

export default GroupsManager;
